import React, { useContext, useEffect, useRef, useState } from 'react';
import { Building2, ExternalLink, MapPin, Pencil, Plus, Power, Users } from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { clientsAPI, sitesAPI } from '../services/api';
import { notify, reportLoadError } from '../utils/notify';
import { can } from '../utils/permissions';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import SiteDialog, { EMPTY_SITE } from './clients/SiteDialog';
import './Clients.css';

// Clients and their sites (posts): the places guards are planned at. Staff
// side, resource "clients". Clients and sites are deactivated, not deleted.

const EMPTY_CLIENT = { company_name: '', contact_name: '', email: '', phone: '', address: '', notes: '' };

const osmLink = (lat, lng) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`;

// The server's field messages ({ field: 'required' }) as one line
const fieldErrors = (error) => {
  const errors = error.response?.data?.errors;
  return errors ? Object.entries(errors).map(([field, msg]) => `${field}: ${msg}`).join('; ') : null;
};

const Clients = () => {
  const { user } = useContext(AuthContext);
  const { t } = useLanguage();
  const canCreate = can(user, 'clients', 'create');
  const canEdit = can(user, 'clients', 'update');

  const [status, setStatus] = useState('active');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [sites, setSites] = useState([]);
  const [clientForm, setClientForm] = useState(null); // { id?, ...fields }
  const [siteForm, setSiteForm] = useState(null); // { id?, client_id, ...fields }
  const [saving, setSaving] = useState(false);

  // bumped after a save to load again
  const [clientsVersion, setClientsVersion] = useState(0);
  const [sitesVersion, setSitesVersion] = useState(0);
  const reloadClients = () => setClientsVersion((n) => n + 1);
  const reloadSites = () => setSitesVersion((n) => n + 1);
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    clientsAPI.list(status === 'all' ? {} : { status })
      .then((res) => { if (!cancelled) setClients(res.data.clients); })
      .catch((error) => reportLoadError(error, tRef.current('Error loading clients'), { main: true }))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [status, clientsVersion]);

  useEffect(() => {
    let cancelled = false;
    if (!openId) {
      setSites([]);
      return undefined;
    }
    clientsAPI.get(openId)
      .then((res) => { if (!cancelled) setSites(res.data.sites); })
      .catch((error) => reportLoadError(error, tRef.current('Error loading sites')));
    return () => { cancelled = true; };
  }, [openId, sitesVersion]);

  const saveClient = async (e) => {
    e.preventDefault();
    const { id, ...data } = clientForm;
    try {
      setSaving(true);
      if (id) await clientsAPI.update(id, data);
      else await clientsAPI.create(data);
      notify(id ? t('Client updated') : t('Client created'), 'success');
      setClientForm(null);
      reloadClients();
    } catch (error) {
      notify(fieldErrors(error) || error.response?.data?.message || t('Error saving the client'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const setClientStatus = async (client, next) => {
    try {
      await clientsAPI.update(client.id, { status: next });
      notify(next === 'active' ? t('Client activated') : t('Client deactivated'), 'success');
      reloadClients();
    } catch (error) {
      notify(error.response?.data?.message || t('Error saving the client'), 'error');
    }
  };

  const setSiteActive = async (site, active) => {
    try {
      await sitesAPI.update(site.id, { active });
      notify(active ? t('Site activated') : t('Site deactivated'), 'success');
      reloadSites();
      reloadClients();
    } catch (error) {
      notify(error.response?.data?.message || t('Error saving the site'), 'error');
    }
  };

  const clientField = (name, label, type = 'text') => (
    <div className="cl-field">
      <label className="field-label" htmlFor={`client-${name}`}>{t(label)}</label>
      <input
        id={`client-${name}`}
        className="input"
        type={type}
        value={clientForm[name] || ''}
        onChange={(e) => setClientForm({ ...clientForm, [name]: e.target.value })}
        required={name === 'company_name'}
      />
    </div>
  );

  return (
    <div className="clients-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Clients & Sites')}</h1>
          <p className="page-sub">{t('The clients and the posts where guards are planned')}</p>
        </div>
        {canCreate && (
          <div className="cl-head-actions">
            <button type="button" className="btn btn--secondary" onClick={() => setClientForm({ ...EMPTY_CLIENT })}>
              <Plus aria-hidden="true" /> {t('New Client')}
            </button>
            <button type="button" className="btn btn--primary" onClick={() => setSiteForm({ ...EMPTY_SITE, client_id: null })}>
              <Plus aria-hidden="true" /> {t('Add site')}
            </button>
          </div>
        )}
      </div>

      <div className="tabbar" role="tablist">
        {[['active', 'Active'], ['inactive', 'Inactive'], ['all', 'All']].map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={status === key}
            className={`tabbar__tab${status === key ? ' is-active' : ''}`} onClick={() => setStatus(key)}>
            {t(label)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="empty">{t('Loading clients...')}</div>
      ) : clients.length === 0 ? (
        <div className="empty">
          <Building2 aria-hidden="true" />
          <p>{t('No clients yet')}</p>
        </div>
      ) : (
        <div className="cl-list">
          {clients.map((client) => (
            <section key={client.id} className={`card cl-card${openId === client.id ? ' is-open' : ''}`}>
              <div className="cl-card__head">
                <button type="button" className="cl-card__toggle" aria-expanded={openId === client.id}
                  onClick={() => setOpenId(openId === client.id ? null : client.id)}>
                  <span className="cl-card__name">{client.company_name}</span>
                  <span className="cl-card__meta">
                    <MapPin aria-hidden="true" /> {t('{active} of {total} sites active', { active: client.active_sites, total: client.sites })}
                  </span>
                </button>
                <StatusBadge status={client.status}>{t(client.status === 'active' ? 'Active' : 'Inactive')}</StatusBadge>
                {canEdit && (
                  <div className="cl-card__actions">
                    <button type="button" className="btn btn--secondary btn--icon" title={t('Edit client')} aria-label={t('Edit client')}
                      onClick={() => setClientForm({ id: client.id, company_name: client.company_name, contact_name: client.contact_name,
                        email: client.email, phone: client.phone, address: client.address, notes: client.notes })}>
                      <Pencil aria-hidden="true" />
                    </button>
                    <button type="button" className="btn btn--secondary btn--icon"
                      title={client.status === 'active' ? t('Deactivate client') : t('Activate client')}
                      aria-label={client.status === 'active' ? t('Deactivate client') : t('Activate client')}
                      onClick={() => setClientStatus(client, client.status === 'active' ? 'inactive' : 'active')}>
                      <Power aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>
              {(client.contact_name || client.email || client.phone) && (
                <p className="cl-card__contact">
                  {[client.contact_name, client.email, client.phone].filter(Boolean).join(' · ')}
                </p>
              )}

              {openId === client.id && (
                <div className="cl-sites">
                  <div className="cl-sites__head">
                    <h2 className="cl-sites__title">{t('Sites')}</h2>
                    {canCreate && client.status === 'active' && (
                      <button type="button" className="btn btn--secondary" onClick={() => setSiteForm({ ...EMPTY_SITE, client_id: client.id })}>
                        <Plus aria-hidden="true" /> {t('New Site')}
                      </button>
                    )}
                  </div>
                  {sites.length === 0 ? (
                    <p className="cl-muted">{t('This client has no sites yet')}</p>
                  ) : (
                    <div className="cl-table-wrap">
                      <table className="data-table cl-table">
                        <thead>
                          <tr>
                            <th>{t('Site')}</th>
                            <th>{t('Location')}</th>
                            <th className="num">{t('Geofence (m)')}</th>
                            <th className="num">{t('Guards needed')}</th>
                            <th>{t('Status')}</th>
                            {canEdit && <th>{t('Actions')}</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {sites.map((site) => (
                            <tr key={site.id}>
                              <td data-label={t('Site')}>
                                <strong>{site.name}</strong>
                                {site.address && <span className="cl-muted cl-block">{site.address}</span>}
                              </td>
                              <td data-label={t('Location')}>
                                {site.latitude === null ? (
                                  <StatusBadge variant="warning">{t('No location')}</StatusBadge>
                                ) : (
                                  <a href={osmLink(site.latitude, site.longitude)} target="_blank" rel="noopener noreferrer" className="cl-point">
                                    {site.latitude.toFixed(6)}, {site.longitude.toFixed(6)} <ExternalLink aria-hidden="true" />
                                  </a>
                                )}
                              </td>
                              <td className="num" data-label={t('Geofence (m)')}>{site.geofence_radius_m}</td>
                              <td className="num" data-label={t('Guards needed')}><Users aria-hidden="true" className="cl-inline-icon" /> {site.guards_needed}</td>
                              <td data-label={t('Status')}>
                                <StatusBadge status={site.active ? 'active' : 'inactive'}>{t(site.active ? 'Active' : 'Inactive')}</StatusBadge>
                              </td>
                              {canEdit && (
                                <td data-label={t('Actions')}>
                                  <div className="cl-row-actions">
                                    <button type="button" className="btn btn--secondary btn--icon" title={t('Edit site')} aria-label={t('Edit site')}
                                      onClick={() => setSiteForm({ id: site.id, client_id: site.client_id, name: site.name, address: site.address || '',
                                        latitude: site.latitude ?? '', longitude: site.longitude ?? '', geofence_radius_m: site.geofence_radius_m,
                                        guards_needed: site.guards_needed, notes: site.notes || '' })}>
                                      <Pencil aria-hidden="true" />
                                    </button>
                                    <button type="button" className="btn btn--secondary btn--icon"
                                      title={site.active ? t('Deactivate site') : t('Activate site')}
                                      aria-label={site.active ? t('Deactivate site') : t('Activate site')}
                                      onClick={() => setSiteActive(site, !site.active)}>
                                      <Power aria-hidden="true" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      <Modal open={Boolean(clientForm)} title={clientForm?.id ? t('Edit Client') : t('New Client')} onClose={() => setClientForm(null)} busy={saving} size="md">
        {clientForm && (
          <form className="cl-dialog" onSubmit={saveClient}>
            {clientField('company_name', 'Name *')}
            <div className="cl-form-row">
              {clientField('contact_name', 'Contact person')}
              {clientField('phone', 'Phone', 'tel')}
            </div>
            {clientField('email', 'Email', 'email')}
            {clientField('address', 'Address')}
            <div className="cl-field">
              <label className="field-label" htmlFor="client-notes">{t('Notes')}</label>
              <textarea id="client-notes" className="textarea" rows="3" value={clientForm.notes || ''}
                onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })} />
            </div>
            <div className="cl-form-actions">
              <button type="button" className="btn btn--secondary" onClick={() => setClientForm(null)}>{t('Cancel')}</button>
              <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? t('Saving...') : t('Save')}</button>
            </div>
          </form>
        )}
      </Modal>

      {siteForm && (
        <SiteDialog
          initial={siteForm}
          canCreateClient={canCreate}
          onClose={() => setSiteForm(null)}
          // the new site's client opens, so the site is in view
          onSaved={(clientId) => { setSiteForm(null); if (clientId) setOpenId(clientId); reloadSites(); reloadClients(); }}
        />
      )}
    </div>
  );
};

export default Clients;
